import React from "react";
import { Typography, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import dayjs from "dayjs";
import {
  BodypartType,
  UserExerciseDto,
} from "@growth-machine-llc/stridist-api-client";
import CalendarDayInfo from "./CalendarDayInfo";
import {
  createCrossLinesGradient,
  createDotsPatternStaggeredSingle,
} from "../WorkoutActivityCalendar";

const DISK_SIZE = 40;

const useStyles = makeStyles((theme) => ({
  day: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: "center",
    position: "relative",
    cursor: "default",
    "& > span": {
      zIndex: 2,
      position: "relative",
      lineHeight: `${DISK_SIZE}px`,
    },
    "&::before": {
      content: '""',
      width: DISK_SIZE,
      height: DISK_SIZE,
      display: "block",
      position: "absolute",
      left: `calc(50% - ${DISK_SIZE / 2}px)`,
      borderRadius: "50%",
      backgroundColor: "transparent",
      zIndex: 1,
    },
    "&$completedCurrentExercise::before": {
      backgroundColor: theme.palette.primary.main,
    },
    "&$hasOtherMatchingExercises::before": {
      backgroundImage: createCrossLinesGradient(theme.palette.primary.main),
    },
    "&$hasOtherBodypartExercises::before": {
      background: createDotsPatternStaggeredSingle(theme.palette.text.disabled),
    },
    "&$notCurrentMonth": { color: theme.palette.text.disabled },
    "&$notCurrentMonth::before": {
      opacity: 0.4,
    },
    "&$hidden": { visibility: "hidden" },
  },
  disabled: {
    color: theme.palette.text.disabled,
    pointerEvents: "none",
  },
  selectedDay: {
    "&::before": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.dark}`,
    },
    fontWeight: 700,
  },

  completedCurrentExercise: {},
  hasOtherMatchingExercises: {},
  notCurrentMonth: {},
  hasOtherBodypartExercises: {},
  hidden: {},
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: theme.spacing(1),
    width: "auto",
    maxWidth: "max-content",
    whiteSpace: "nowrap",
    padding: 0,
  },
  arrow: {
    color: theme.palette.background.paper,
  },
}));

interface CalendarDayProps {
  dayItem: any;
  isLoadingInitial: boolean;
  selectedBodypart: BodypartType;
  onExerciseSelect?: (exercise: UserExerciseDto) => void;
  onDaySelect?: (dayItem: any) => void;
  onShowMore?: (dayItem: any) => void;
  isSelected?: boolean;
  detailsOpened?: boolean;
}

const CalendarDay: React.FC<CalendarDayProps> = ({
  dayItem,
  isLoadingInitial,
  selectedBodypart,
  onExerciseSelect,
  onDaySelect,
  isSelected,
  detailsOpened,
  onShowMore,
}) => {
  const s = useStyles();

  const {
    date,
    day,
    isCurrentMonth,
    completedCurrentExercise,
    hasOtherMatchingExercises,
    hasOtherBodypartExercises,
  } = dayItem;

  const formattedDate = dayjs(date).format("MMMM D, YYYY");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const shouldShowTooltip =
    !isMobile &&
    (completedCurrentExercise ||
      hasOtherMatchingExercises ||
      hasOtherBodypartExercises) &&
    isCurrentMonth &&
    !detailsOpened;

  const getDayTextColor = () => {
    if (completedCurrentExercise) {
      return theme.palette.getContrastText(theme.palette.primary.main);
    }

    if (hasOtherBodypartExercises) {
      return theme.palette.text.primary;
    }
    return isCurrentMonth
      ? theme.palette.text.primary
      : theme.palette.text.disabled;
  };

  const dayContent = (
    <Typography
      data-date={date}
      onClick={() => onDaySelect && isCurrentMonth && onDaySelect(dayItem)}
      className={clsx(s.day, {
        [s.hasOtherMatchingExercises]:
          hasOtherMatchingExercises && !completedCurrentExercise,
        [s.hasOtherBodypartExercises]:
          hasOtherBodypartExercises && !completedCurrentExercise,
        [s.completedCurrentExercise]: completedCurrentExercise,
        [s.notCurrentMonth]: !isCurrentMonth,
        [s.disabled]: isLoadingInitial,
        [s.selectedDay]: isSelected,
      })}
      sx={{ color: getDayTextColor() }}
    >
      <span>{day}</span>
    </Typography>
  );

  return shouldShowTooltip ? (
    <Tooltip
      title={
        <CalendarDayInfo
          dayItem={dayItem}
          selectedBodypart={selectedBodypart}
          formattedDate={formattedDate}
          onExerciseSelect={onExerciseSelect}
          onShowMore={() => onShowMore && onShowMore(dayItem)}
          isTooltip={true}
        />
      }
      arrow
      placement="top"
      disableInteractive={false}
      PopperProps={{
        modifiers: [
          { name: "flip", enabled: true },
          { name: "preventOverflow", enabled: true },
        ],
      }}
      classes={{
        tooltip: s.tooltip,
        arrow: s.arrow,
      }}
    >
      {dayContent}
    </Tooltip>
  ) : (
    dayContent
  );
};

export default CalendarDay;
